import { setParamsGeronimoInSession } from './../../../../tools/utils/geronimo';
import { IUrlInfo } from './../../../../tools/models/url-info/url-info.model';
import { VehicleService } from './core/service/vehicle/vehicle.service';
import { RevisionServicesService } from '../app/core/service/revision-services/revision-services.service';
import { Location } from '@angular/common';
import { Component, OnInit, inject } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { ServiceStorage } from './core/service/storage-service/service-storage.service';
import { isWebAccess, timeIsOver } from './utils/utils';
import { LoaderService } from './shared/loader/loader.service';
import { CountryService } from './core/service/country/country-service.service';
import { NotificationService } from '@agenda-customer-ui/shared';
import { SESSION_FORD_PROTECT, STEPS } from './utils/constants';
import { ScheduleService } from './core/service/schedule/schedule.service';
import { RevisionModel } from './core/models/revision/revision.model';
import { ScheduleServices } from './core/models/schedule/schedule-services.model';
import { forkJoin, firstValueFrom, of } from 'rxjs';
import { ScheduledServicesModel } from './core/models/schedule/scheduledServicesModel';
import { 
  AuthService, 
  CountryRulesService, 
  DynatraceService, 
  FeatureToggleCodes, 
  FeatureToggleService, 
  HistoryService, 
  NewScheduleService, 
  QuotationsService, 
  RecallOwnerService } from '@agenda-customer-ui/data-access';
import { IolmKitModel } from './core/models/revision/iolm-kit.model';
import { FdsCurrencyPipe } from './shared/pipes/currency/currency-status.pipe';
import { Source, getLangByCountry, AppMaintenancePageComponent } from '@agenda-customer-ui/shared';
import { CAR_MODELS, SchedulingModalityEnum, StorageKeys } from '@core/enums';
import { IRecall, IResponseModel, IUserProfileResponse, IVehicleFromUsers } from '@core/interface';
import { API, environment } from '@core/envs';
import { GetCountryService, GoogleMapsApiService, MyGarageService, UserService as CoreUserService } from '@core/services';
import { RouterModule } from '@angular/router';

@Component({
  standalone: true,
  selector: 'agenda-customer-ui-root',
  templateUrl: './app.component.html',
  imports: [RouterModule, AppMaintenancePageComponent],
  providers: [
    FdsCurrencyPipe,
    CountryRulesService,
    {
      provide: 'url',
      useValue: environment.getRoute(API.backoffice, true)
    }
  ],
})
export class AppComponent implements OnInit {
  readonly #dynatraceService = inject(DynatraceService);
  readonly #historyServices = inject(HistoryService);
  private readonly countryRules = inject(CountryRulesService);
  private readonly recallOwnerService = inject(RecallOwnerService);
  private readonly googleMapsApisService = inject(GoogleMapsApiService);
  readonly #myGarageService = inject(MyGarageService);
  readonly #coreUserService = inject(CoreUserService);
  private readonly location = inject(Location);

  isReschedule!: boolean;
  isWebAccess = false;
  language!: string;
  infosFromPath!: IUrlInfo;
  showProject = true;

  hasInvalidPayment = false;

  constructor(
    private auth: AuthService,
    private serviceStorage: ServiceStorage,
    private translate: TranslateService,
    public loaderService: LoaderService,
    private revisionService: RevisionServicesService,
    private countryService: CountryService,
    private vehicleService: VehicleService,
    private notificationService: NotificationService,
    private scheduleService: ScheduleService,
    private revisionServicesService: RevisionServicesService,
    private quotationService: QuotationsService,
    private fdsCurrencyPipe: FdsCurrencyPipe,
    private featureToggle: FeatureToggleService,
    private newScheduleService: NewScheduleService,
    private getCountryService: GetCountryService,
    private myGarageService: MyGarageService
  ) { }

  async ngOnInit(): Promise<void> {
    await this.#initSessionDefault();

    this.isReschedule = this.#isRescheduleSession();

    const { country, vin, dealerId, scheduleId, advisorId, scheduleDate, scheduleHour, hasPaymentMethod } = this.infosFromPath;
    this.#dynatraceService.setVariableStorage('vin', vin);
    this.countryRules.loadMasks(country);

    // Possibly useless, as it's only currently used for maintenance mode, which is fetched in agenda-users and does not land in this page if it's active
    const featureToggleResponse = await firstValueFrom(this.featureToggle.getList(country));
    this.showProject = featureToggleResponse?.OSR_FLOW_DISPLAY_SCHEDULE?.active || false;
    localStorage.setItem('maintenanceWindow', `${!this.showProject}`);

    const isTimeOver = timeIsOver(this.auth.shouldClear(country, vin, dealerId ?? ''));
    const hasDealerId = !!((dealerId && dealerId !== 'undefined' || undefined));

    this.countryService.getRulesByCountry(country);
    this.vehicleService.getIsCommercialVehicle(vin, country).subscribe((data: number) => {
      this.serviceStorage.setIsCommercialVehicle(data !== 0);
    });

    // Fire-and-forget: telemetry must never block the scheduling flow.
    // VehicleService.getTMC already catches/logs errors and resolves to null, so no error handler is needed here.
    this.vehicleService.getTMC(vin).subscribe();

    this.revisionService.getConnectivityStatus().subscribe();

    if (this.isReschedule && scheduleId && advisorId && scheduleDate && scheduleHour && dealerId) {
      this.serviceStorage.setLoadedReschedule(true);
      await Promise.all([
        this.#getRevisionV4(dealerId ?? '', hasDealerId),
        this.#initInfosSystem(vin, country),
      ]);

      await this.#handleReschedule(scheduleId, vin, country, advisorId, scheduleDate, scheduleHour, dealerId, hasPaymentMethod);

      if(!this.hasInvalidPayment) {
        this.loaderService.isLoadingInfoUser.next(false);
       }
      return;
    }

    if (isTimeOver) {
      const currentTime = new Date().getTime();
      localStorage.setItem('@Ford-last-login-time', '' + currentTime);

      await this.#initInfosSystem(vin, country);
      this.#getRevisionV4(dealerId ?? '', hasDealerId);
    } else {
      this.#recoverylocalStorage();
    }    

    this.loaderService.isLoadingInfoUser.next(false);
  }

  async #initInfosSystem(vin: string, country: string): Promise<void> {
    let recall$;
    try {
      const recallObservable$ = this.recallOwnerService.getRecall(vin, country);
      recall$ = await firstValueFrom(recallObservable$);
      await this.#setRecall(recall$);
    } catch (error) {
      console.error('Error retrieving recall:', error);
      // Handle the error gracefully, e.g. set recall to an empty array
    }

    this.#historyServices.getRevisionHistory(vin, country).subscribe();

    const vehicleVSS$ = this.vehicleService.getVehicleDetailByVin(vin, country);
    const vehicle$ = this.vehicleService.getVehiclePlateByVin(vin, country);

    // Dispara as duas chamadas em paralelo, pois nao dependem uma da outra
    const userProfile$ = this.#coreUserService.loadUserProfile();
    const vehicles$ = firstValueFrom(forkJoin([vehicle$, vehicleVSS$]));

    // Aguarda o carregamento dos veiculos do usuario antes de gravar no storage,
    // evitando race condition que deixava o preferredDealer indisponivel
    const userVehicles$ = this.myGarageService.loadUserVehicles();

    try {
      const [userProfile, result] = await Promise.all([userProfile$, vehicles$, userVehicles$]);

      this.serviceStorage.setEngineCode(result[0].response?.engineCode);
      try {
        await this.#userInit(userProfile);
        await this.#setInfosVehicle(result[0]);

        if (result[1]) {
          const { fuelType } = result[1];
          this.serviceStorage.setIsElectricalVehicle(fuelType);
        }

      } catch (error) {
        this.#dynatraceService.setVariableStorage('model', '');
        this.#dynatraceService.setVariableStorage('modelGroup', '');
        this.#dynatraceService.setVariableStorage('modelName', '');
        this.#dynatraceService.setVariableStorage('modelYear', '');
        this.#dynatraceService.setVariableStorage('plate', '');

        return console.error('Error:', error);
      }
    } catch (error) {
      console.error('Erro:', error);
    }
  }

  async #handleReschedule(scheduleId: string, vin: string, country: string, advisorId: string, scheduleDate: string, scheduleHour: string, dealerId: string, hasPaymentMethod: boolean): Promise<void> {
    try {
      // Obtém schedulesOpens
      const schedulesOpens = await firstValueFrom(this.scheduleService.getScheduledServices(vin, country));

      // Obtém scheduleQuotation
      const scheduleQuotation = await firstValueFrom(
        this.quotationService.getScheduleQuotation(advisorId, scheduleDate, scheduleHour, dealerId, country)
      );

      const customerWaiting = scheduleQuotation?.response?.customerWaitingInDealer;
      if (customerWaiting !== undefined && customerWaiting !== null) {
        sessionStorage.setItem(
          StorageKeys.WAITING_OPTION,
          customerWaiting === 'Y' ? 'wait_at_dealership' : 'return_after_completion'
        );
      }

      const schedule = this.findSchedule(schedulesOpens?.response, +(scheduleId ?? ''));

      if (hasPaymentMethod && !this.hasValidPayment(schedule)) {
        this.hasInvalidPayment = true;

        this.notificationService.add({
          text: this.translate.instant('notification.payment-error'),
          severity: 'ERROR',
          timeout: 4000,
        });

        setTimeout(() => this.location.back(), 4000);
        return;
      }

      if (schedule?.pickupDelivery) {
        this.newScheduleService.setModality(SchedulingModalityEnum.PDL);
      } else if (schedule?.winchService) {
        this.newScheduleService.setModality(SchedulingModalityEnum.WINCH);
      } else if (schedule?.mobileService) {
        this.newScheduleService.setModality(SchedulingModalityEnum.MSV);
      } else {
        this.newScheduleService.setModality(SchedulingModalityEnum.DEALER);
      }

      this.serviceStorage.scheduleToCancel = schedule ?? null;

      if (schedule) {
        const remoteServiceID = this.findRemoteService(schedule);

        localStorage.setItem('@Ford-selected-option-step-2', `${remoteServiceID ?? 1}`);
        localStorage.setItem('@Ford-selected-date', `${schedule.scheduleDate}T00:00:00`);
        localStorage.setItem('@Ford-selected-hour', `${schedule.scheduleHour}`);
        localStorage.setItem('@Ford-selected-advisor', `${schedule.advisorId}`);
        localStorage.setItem('@Ford-selected-advisor-name', `${schedule.advisorName}`);

        const revisionFound = this.findServiceRevision(schedule);
        const { offeredServices, revisionHistory } = this.serviceStorage.getRevisionListStorage();
        const revisionSelected = revisionFound ? this.findServiceRevisionStorage(revisionFound, offeredServices) : undefined;

        let infosRevision: RevisionModel[] = [];
        if (revisionSelected) {
          infosRevision = await this.getInfosRevision(revisionSelected.months, dealerId);
        }

        const iolmService = scheduleQuotation?.response?.requestedItems?.services?.find((service) => this.isIolmService(service));
        if (iolmService) {
          localStorage.setItem('@Ford-reschedule-selected-iolm', JSON.stringify(iolmService?.revisionParts));
          const iolmServiceNew = new ScheduleServices();
          iolmServiceNew.servicePrice = 0;
          iolmServiceNew.serviceId = iolmService.serviceId;
          iolmServiceNew.serviceName = iolmService.serviceName;
          iolmServiceNew.dmsCode = iolmService.dmsCode;

          this.serviceStorage.iolmKit = [];
          const iolmParts: IolmKitModel[] = [];
          iolmService?.revisionParts.forEach((item) => {
            iolmServiceNew.servicePrice += Number(item.price);
            iolmParts.push({
              country: country,
              partNumber: item.partCode,
              idMaintenance: 0,
              vin: '',
              tma: '',
              engineCode: '',
              modelYear: '',
              monthsWarranty: 0,
              maintenanceCode: '',
              servicePrice: item.price.toString(),
              iolmWersCode: '',
              iolmMileage: 0,
              iolmMonths: '',
              longPartNumber: item.longPartDesc,
              shortPartNumber: '',
              qtdy: '',
              price: item.price.toString(),
              kmOilKit: 0,
              kmLastExchange: 0,
              kmMinimumInterval: 0,
              kmMaximumInterval: 0,
              monthOilKit: 0,
              monthLastExchange: 0,
              monthMinimumInterval: 0,
              monthMaximumInterval: 0,
              kmObrigatorio: false,
              kmRecomendado: false,
              mesObrigatorio: false,
              mesRecomendado: false,
              suggestion: ''
            });
            this.serviceStorage.iolmKit.push({
              title: item.longPartDesc,
              value: this.fdsCurrencyPipe.transform(Number(item.price)),
            });
          });
          this.serviceStorage.setIolmParts(iolmParts);
          this.serviceStorage.setIolmService(iolmServiceNew);
        }

        const { optionalServicesSelected, totalPrice } = this.calculateOptionalServices(schedule, offeredServices);
        this.serviceStorage.changeServices(
          revisionHistory,
          optionalServicesSelected,
          totalPrice,
          true,
          infosRevision[0]?.price,
          revisionSelected || new ScheduleServices(),
          infosRevision[0]?.revisionPartVOs,
          infosRevision,
          []
        );
        this.serviceStorage.setDetails(schedule?.customerRemarks || '');
        const step = hasPaymentMethod 
          && this.serviceStorage.paymentFeatureToggleEnabled 
          && this.serviceStorage.reschedule ? STEPS.PICKUPANDELIVERY : STEPS.DEALERSHIP;
        this.serviceStorage.setStep({ value: true, step });
      }
    } catch (error) {
      console.error(error);
    }
  }


  private findSchedule(schedulesOpens: ScheduledServicesModel[] | undefined, scheduleId: number): ScheduledServicesModel | undefined {
    return schedulesOpens?.find(schedule => schedule.scheduleId === scheduleId);
  }

  private hasValidPayment(schedule: ScheduledServicesModel | undefined): boolean {
    return schedule?.totalAlreadyPaid != null && schedule.totalAlreadyPaid >= 0;
  }

  private findRemoteService(schedule: ScheduledServicesModel): number | undefined {
    return schedule?.services.find(service => service.pickupDelivery === 'Y' || service.mobileService === 'Y')?.serviceId;
  }

  private findServiceRevision(schedule: ScheduledServicesModel): number | undefined {
    return schedule?.services.find(service => service.revision === 'Y')?.serviceId;
  }

  private findServiceRevisionStorage(serviceId: number, offeredServices: ScheduleServices[]): ScheduleServices | undefined {
    if (offeredServices.length > 0) {
      return offeredServices.find((service) => service.serviceId === serviceId);
    }
    return;
  }

  private calculateOptionalServices(schedule: ScheduledServicesModel, offeredServices: ScheduleServices[]): { optionalServicesSelected: ScheduleServices[], totalPrice: number } {
    const clearServices = schedule?.services.filter((s) =>
      s.revision === 'N' &&
      (!s.pickupDelivery || s.pickupDelivery === 'N') &&
      (!s.mobileService || s.mobileService === 'N') &&
      !s.serviceName?.toUpperCase().includes('RECALL')
    );
    const optionalServicesSelected: ScheduleServices[] = [];
    let totalPrice = 0;

    offeredServices?.forEach((service: ScheduleServices) => {
      clearServices?.find((option) => {
        if (service?.serviceId === option?.serviceId && (service.serviceName.toLowerCase() !== 'recall' && service.serviceName.toLowerCase() !== 'campañas de servicio' && !this.isIolmService(service))) {
          totalPrice += service.servicePrice;
          optionalServicesSelected.push(service);
        }
      });
    });

    return { optionalServicesSelected, totalPrice };
  }

  isIolmService(service: any): boolean {
    return service.serviceName.toLowerCase().includes('kit complementar');
  }

  #isRescheduleSession(): boolean {
    this.serviceStorage.reschedule = sessionStorage.getItem('reschedule') !== null;
    return this.serviceStorage.reschedule;
  }

  async #recoverylocalStorage() {
    if ('@Ford-selected-revision-and-optional' in localStorage) {
      this.serviceStorage.setStep({ value: true, step: STEPS.SERVICES });
      localStorage.setItem('@Ford-last-step', JSON.stringify({ value: true, step: STEPS.SERVICES }));
    } else if ('@Ford-selected-option-step-2' in localStorage) {
      this.serviceStorage.setStep({ value: true, step: STEPS.PICKUPANDELIVERY });
    }

    if ('@Ford-selected-revision-and-optional' in localStorage) {
      const objectService = JSON.parse(localStorage.getItem('@Ford-selected-revision-and-optional') as string);

      this.serviceStorage.changeServices(
        objectService.revisionsHistory,
        objectService.offeredServices,
        objectService.totalPrice,
        objectService.onRequest,
        objectService.revisionPrice,
        objectService.selectedRevision,
        objectService.revisionParts,
        objectService.revisionInfo,
        objectService.campaigns
      );
      this.serviceStorage.setDetails(this.serviceStorage.getDetails() || '');
    }

    this.retriveUser();

    //vehicleModel
    const vehicleModel = JSON.parse(localStorage.getItem('@Ford-vehicleModel') as string);
    this.serviceStorage.setVehicleDate(vehicleModel);

    const vehicledata = JSON.parse(localStorage.getItem('@Ford-vehicleData') as string);
    if (vehicledata) {
      const { fuelType } = vehicledata;
      this.serviceStorage.setIsElectricalVehicle(fuelType);
    }

    //recall
    if ('@Ford-recall' in localStorage) {
      const { recall, recallService, hasRecall } = JSON.parse(localStorage.getItem('@Ford-recall') as string);
      this.serviceStorage.changeRecall(recall, recallService, hasRecall);
    }

    //@Ford-userProfile
    if ('@Ford-userProfile' in localStorage) {
      this.serviceStorage.user = JSON.parse(localStorage.getItem('@Ford-userProfile') as string);
    }

    this.notificationService.add({
      text: this.getLangByCountry(this.infosFromPath.country),
      severity: 'SUCCESS',
      timeout: 5000,
    });
  }

  async #initSessionDefault(): Promise<void> {
    const pathPromise = new Promise((resolve) => {
      this.auth.getParametersFromPath().subscribe(
        (response) => {
          const isConfirmationStep = response['step'] === 'confirmation';
          sessionStorage.setItem('enableNewStepServices', response['enableNewStepServices']);

          // convert string to boolean
          const hasPaymentMethod = response['hasPaymentMethod'] === 'true';

          if (response['reschedule'] === 'true' || response['updateStorage'] === 'true') {
            localStorage.clear();
            sessionStorage.clear();
          }

          this.infosFromPath = {
            source: isConfirmationStep ? localStorage.getItem('@Ford-source') : response['source'],
            country: isConfirmationStep ? localStorage.getItem('@Ford-country') : response['country'],
            vin: response['vin'],
            scheduleId: response['scheduleId'],
            reschedule: response['reschedule'],
            dealerId: response['dealerId'],
            divisionId: response['divisionId'],
            advisorId: response['advisorId'],
            scheduleDate: response['scheduleDate'],
            scheduleHour: response['scheduleHour'],
            coordinate: {
              lat: response['lat'],
              lng: response['lng'],
            },
            geronimo: {
              country: response['geronimoCountry'],
              dealerId: response['geronimoDealerId']
            },
            hasPaymentMethod: response['hasPaymentMethod'] === 'true'
          };

          this.getLang(this.infosFromPath.country);

          this.auth.urlInfo = this.infosFromPath;
          this.auth.tokens = {
            cat: isConfirmationStep ? localStorage.getItem('@Ford-cat') : response['cat']
          };

          this.#setStorage();
          this.setScheduleSourceInStorage(this.infosFromPath.source);

          if (isConfirmationStep) {
            this.serviceStorage.setStep({ value: true, step: STEPS.CONFIRMATION });
            resolve(true);
            return;
          }
          resolve(true);
        });
    });

    await pathPromise;
  }

  #setStorage(): void {
    // Detect a vehicle change (different VIN than what was previously in this
    // session) so any Ford Protect data cached for the prior vehicle never
    // leaks into the new one's flow (e.g. stale data reaching step 5).
    const previousVin = sessionStorage.getItem('vin');
    if (previousVin && previousVin !== this.infosFromPath.vin) {
      sessionStorage.removeItem(SESSION_FORD_PROTECT);
    }

    localStorage.setItem('vin', this.infosFromPath.vin);
    localStorage.setItem('country', this.infosFromPath.country);
    localStorage.setItem('@Ford-country', this.infosFromPath.country);
    localStorage.setItem('@Ford-selected-date', '');
    localStorage.setItem('@Ford-selected-hour', '');
    localStorage.setItem('@Ford-selected-advisor', '');
    localStorage.setItem('@Ford-selected-advisor-name', '');
    localStorage.setItem('@Ford-cat', this.auth.tokens.cat);
    localStorage.setItem('@Ford-source', this.infosFromPath.source);

    sessionStorage.setItem('dealerList', '[]');
    sessionStorage.setItem('dealerbyFordPass', '[]');
    this.getCountryService.setCountry(this.infosFromPath.country);
    sessionStorage.setItem('divisionId', this.infosFromPath.divisionId ?? '');
    sessionStorage.setItem('authToken', this.auth.tokens.cat);
    sessionStorage.setItem('dealerId', this.infosFromPath.dealerId ?? '');
    sessionStorage.setItem('vin', this.infosFromPath.vin);
    sessionStorage.setItem('source', this.infosFromPath.source);

    const sourceCode = this.getSourceCode(this.infosFromPath.source);
    this.serviceStorage.setScheduleSource(sourceCode ?? Source.web);
    this.serviceStorage.changeDelaerId(this.infosFromPath.dealerId ?? '');
    this.serviceStorage.schedule.division = this.infosFromPath.divisionId ?? '';
    this.serviceStorage.changeCustomerVin(this.infosFromPath.vin);
    this.serviceStorage.changeCustomerCountry(this.infosFromPath.country);

    if (this.infosFromPath.reschedule && this.infosFromPath.scheduleId) {
      sessionStorage.setItem('reschedule', this.infosFromPath.reschedule);
      sessionStorage.setItem('scheduleId', this.infosFromPath.scheduleId);
      sessionStorage.setItem('hasPaymentMethod', this.infosFromPath.hasPaymentMethod.toString());
      this.serviceStorage.setHasPaymentMethod(this.infosFromPath.hasPaymentMethod);
    } else {
      sessionStorage.removeItem('reschedule');
      sessionStorage.removeItem('scheduleId');
      sessionStorage.removeItem('hasPaymentMethod');
    }

    if (this.infosFromPath.geronimo) {
      setParamsGeronimoInSession(this.infosFromPath.geronimo);
    }
    sessionStorage.removeItem(StorageKeys.WAITING_OPTION);
  }

  private getSourceCode(value: string) {
    return Object.entries(Source).find(([key]) => key === value)?.[1];
  }

  private getLangByCountry = (country: string) => {
    switch (country) {
      case 'BRA':
        return 'Recuperamos a informação do seu último acesso';
      case 'ARG':
        return 'Recuperamos la información de tu ultimo acceso';
      default:
        return 'We recover your latest access information';
    }
  }

  private retriveUser = () => {
    try {
      const user = JSON.parse(localStorage.getItem("@Ford-userProfile") as string);
      if (user) {
      this.serviceStorage.changeCustomerNationalId(user.nationalIdentification?.value ?? '');
        this.loaderService.isLoadingInfoUser.next(false);
      }
    } catch (error) {
      console.error('Error loading user profile:', error);
    }
  }

  private getInfosRevision = async (months: number, dealerId: string): Promise<RevisionModel[]> => {
    const promise = new Promise((resolve) => {
      const isIolm = this.featureToggle.list[FeatureToggleCodes.STF_IOLM]?.active ? this.#checkIfIsIOLM() : false;
      const iolm = isIolm ? "Y" : "N";
      this.serviceStorage.setIsIolm(isIolm);
      months = isIolm ? 12 : months;
      
      this.revisionServicesService.getRevision(months, iolm, dealerId).subscribe(
        (response) => {
          resolve(response?.response ?? [])
        }
      );
    });

    let infos: RevisionModel[] = [];
    try {
      await promise.then(data => {
        infos = data as RevisionModel[]
      });
    } catch (err) {
      console.error("Error: ", err);
    }

    return infos;
  }

  #checkIfIsIOLM(): boolean {
    const vehicleData = localStorage.getItem('@Ford-vehicleModel');
    if (!vehicleData || vehicleData === 'null') {
      return false;
    }

    if (this.serviceStorage.getIsElectricalVehicle()) {
      return false;
    }

    const vehicle = JSON.parse(vehicleData);
    const modelNameUpper: string = vehicle.modelName.toUpperCase();
    const isTerritory = modelNameUpper.includes(CAR_MODELS.TERRITORY);
    const isRanger = modelNameUpper.includes(CAR_MODELS.RANGER);
    const isModelYear2023OrPlus = vehicle.modelYear >= 2023;

    if (isRanger && isModelYear2023OrPlus) {
      return true;
    }

    if (isTerritory || !isModelYear2023OrPlus) {
      return false;
    }

    return true;
  }

  #getRevisionV4 = async (dealerId: string, hasDealerId: boolean) => {
    const id = Number(dealerId);
    if (hasDealerId) {
      await new Promise((resolve) => {
        this.revisionService.getServicesByFeatureFlag(id)
          .subscribe({
            next: (response) => {
              localStorage.setItem('@Ford-revision-v4', JSON.stringify(response.response));              
              resolve("Complete")
            },
            error: (e) => {
              console.error(e);
            }
          });
      });
    }
  }

  #setRecall = async (response: IResponseModel<IRecall[]>) => {
    const recall = response.response;
    const hasRecall = (response.message !== 'NO_CONTENT' && response.response && response.response.length !== 0);

    localStorage.setItem(
      '@Ford-recall',
      JSON.stringify({ recall, recallService: [], hasRecall })
    );

    this.serviceStorage.changeRecall(recall, [], hasRecall);
  }

  #setInfosVehicle = async (response: any) => {
    localStorage.setItem('@Ford-vehicleModel', JSON.stringify(response.response));
    this.#dynatraceService.setVariableStorage('model', response?.response?.model);
    this.#dynatraceService.setVariableStorage('modelGroup', response?.response?.modelGroup);
    this.#dynatraceService.setVariableStorage('modelName', response?.response?.modelName);
    this.#dynatraceService.setVariableStorage('modelYear', response?.response?.modelYear);
    this.#dynatraceService.setVariableStorage('plate', response?.response?.plate);
    this.serviceStorage.setVehicleDate(response?.response);
  }

  #userInit = async (userProfile: IUserProfileResponse) => {
    localStorage.setItem(
      '@Ford-lang',
      userProfile.languages?.preferredLanguage ?? ''
    );
    localStorage.setItem(
      '@Ford-userProfile',
      JSON.stringify(userProfile)
    );

    this.serviceStorage.user = userProfile;

    localStorage.setItem('@Ford-userVehicles', JSON.stringify(this.#myGarageService.userVehicles()));
    sessionStorage.setItem('userVehicles', JSON.stringify(this.#myGarageService.userVehicles()));

    const email = userProfile.emails?.email ?? '';
    this.#dynatraceService.setVariableStorage('email', email);

    if (this.#coreUserService.hasAddress()) {
      const { addressLine1, addressLine2, postalCode, city } = userProfile.address!;
      const userAddress = `${addressLine1}, ${addressLine2}, ${postalCode} - ${city}`;

      const position = await firstValueFrom(this.googleMapsApisService.geocodeBy('address', userAddress));
      const result = position?.results?.[0];
      if (result) localStorage.setItem('@Ford-coordinate', JSON.stringify(result.geometry.location));
    }

    if (userProfile) {
      this.#coreUserService.saveUserProfile(userProfile);
    }
  }

  setScheduleSourceInStorage(source: string) {
    if (!source) {
      this.isWebAccess = true;
      return;
    }

    const sourceCode = this.getSourceCode(source);
    sessionStorage.setItem('scheduleSource', sourceCode ?? Source.web);
    this.isWebAccess = isWebAccess();
  }

  getLang(country: string) {
    const tempLang = sessionStorage.getItem('tempLang');
    this.language = getLangByCountry(country || '');

    if (tempLang) {
      this.language = tempLang;
    }

    this.translate.setDefaultLang(this.language);
    this.translate.use(this.language);
  }
}
